import { useState, useEffect } from "react";
import { useWrapRequest } from "utils";
import { useNavigate } from "react-router-dom";
import { CartModel, ProductsBundlesModel } from "atoms/models";
import { getCustomerSummaryPage } from "services/get-getCustomerSummaryPage";
import { useRecoilState, useSetRecoilState } from "recoil";
import { cartState, overlayState, toastState, showAdvertisingModalState, advertisingModalDataState, adActionsState } from "atoms";
import { getGetProductContentPage } from "services/get-getProductContent";
import SecuritySection from "components/CustomerSummary/SecuritySection";
import InternetSection from "components/CustomerSummary/InternetSection";
import TelevisionSection from "components/CustomerSummary/TelevisionSection";
import PhoneSection from "components/CustomerSummary/PhoneSection";
import { postCustomerSummaryPage } from "services/post-postCustomerSummary";
import { CustomerSummaryRequestModel } from "atoms/models";
import { gtmCheckIfExistingCustomer } from "utils/googleTagManager";
import InternetIcon from "images/internet-icon.png";
import TVIcon from "images/tv-icon.png";
import SecurityIcon from "images/security-icon.png";
import PhoneIcon from "images/phone-icon.png";
import AdvertisingModal from "components/Customize/AdvertisingModal";
import "components/CustomerSummary/customer-summary.scss";
import { postModalAd } from "services/post-modalAd";
import CancelServicesModal from "components/CustomerSummary/CancelServicesModal";

interface CustomerSummaryProducts {
  productId: number;
  coreType: string;
  hasService: boolean;
  productName: string;
  productMessage: string;
  speedUp: number;
  speedUpUnits: string;
  speedDown: number;
  speedDownUnits: string;
  canUpgrade: boolean;
  upgradeMessage: string;
}

const CustomerSummary = () => {
  const [customerSummaryContent, setCustomerSummaryContent] = useState<Array<ProductsBundlesModel>>();
  const [showAdvertisingModal, setShowAdvertisingModal] = useRecoilState(showAdvertisingModalState);
  const [cancelFeatureFlag, setCancelFeatureFlag] = useState(false);
  const setAdvertisingModalData = useSetRecoilState(advertisingModalDataState);
  const setAdActions = useSetRecoilState(adActionsState);
  const [cart, setCart] = useRecoilState(cartState);
  const [toast, setToast] = useRecoilState(toastState);
  const setShowOverlay = useSetRecoilState(overlayState);
  const wrapRequest = useWrapRequest();
  const navigate = useNavigate();

  const getContentAndUpgradesForCurrentProducts = async (
    customerSummaryProducts: Array<CustomerSummaryProducts>,
    cart: CartModel,
    currentPlanHelpCenterLink: string
  ) => {
    try {
      const fetchCustomerSummaryContent = async (customerSummaryProducts: Array<CustomerSummaryProducts>) => {
        return Promise.all(
          customerSummaryProducts.map(async (product: CustomerSummaryProducts) => {
            const customerSummaryContent = await getGetProductContentPage(product.productId, "customize");

            const matchingCartProduct = cart?.currentServices?.monthly?.find((item: { serviceCoreTypeName: string }) => {
              // Handle INT and HSI interchangeably. Sometimes the API returns either HSI or INT for core type.
              if (product.coreType === "INT" || product.coreType === "HSI") {
                return item.serviceCoreTypeName === "HSI" || item.serviceCoreTypeName === "INT";
              }

              // Handle VID and TV interchangeably
              if (product.coreType === "VID" || product.coreType === "TV") {
                return item.serviceCoreTypeName === "TV" || item.serviceCoreTypeName === "VID";
              }
              // Direct match for other core types (SEC, TEL, etc.)
              return item.serviceCoreTypeName === product.coreType;
            });

            // Use matching cart product or fallback to original product
            const cartProduct = matchingCartProduct
              ? { ...matchingCartProduct, currentPlanHelpCenterLink }
              : { ...product, currentPlanHelpCenterLink };

            const { upgrades = [], termPrice = [], currentProductLabel = [] } = cartProduct;

            return {
              ...cartProduct,
              ...customerSummaryContent?.contentItemEcommerce,
              upgrades,
              termPrice,
              currentProductLabel,
              hasService: product.hasService,
              canUpgrade: product.canUpgrade,
              coreType: product.coreType,
            };
          })
        );
      };

      const customerSummaryProductsWithContent: Array<CustomerSummaryProducts> = await fetchCustomerSummaryContent(customerSummaryProducts);

      setCustomerSummaryContent(customerSummaryProductsWithContent);
      setShowOverlay(false);
    } catch (error) {
      setShowOverlay(false);
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message: `Error fetching browseContent: ${error}`,
      });
    }
  };

  useEffect(() => {
    wrapRequest(async () => {
      const getCustomerSummaryPageResponse = await getCustomerSummaryPage();

      const links = getCustomerSummaryPageResponse["_links"];
      const WINDOW_LOCATION = "ec:windowLocation";

      if (getCustomerSummaryPageResponse._embedded) {
        const cart = getCustomerSummaryPageResponse._embedded["ec:cart"];
        const customerSummary = getCustomerSummaryPageResponse._embedded["ec:customer-summary"];
        const customerSummaryProducts = customerSummary.productsBundles;
        const cancelFeatureFlag = getCustomerSummaryPageResponse._embedded["ec:cancel-feature-flag"];
        const productsFiltered = customerSummaryProducts.filter((item: { coreType: string }) => item.coreType !== "bundle");

        getContentAndUpgradesForCurrentProducts(productsFiltered, cart, customerSummary.currentPlanHelpCenterLink);

        setCart(cart);
        setCancelFeatureFlag(cancelFeatureFlag === undefined ? true : cancelFeatureFlag);
        gtmCheckIfExistingCustomer(cart);
      } else {
        if (links[WINDOW_LOCATION].href.includes("https")) {
          window.location.href = links[WINDOW_LOCATION].href;
        } else {
          navigate(links[WINDOW_LOCATION].href);
        }
      }
    });
    // eslint-disable-next-line
  }, [navigate, setCart]);

  useEffect(() => {
    window.scroll(0, 0);
  });

  const postCustomerSummaryRequest = async (serviceType: string, upgrade: { isPremiumWifi: boolean; productId: number }) => {
    let postRequestBody: CustomerSummaryRequestModel;

    if (upgrade) {
      if (serviceType === "HSI") {
        if (!upgrade.isPremiumWifi) {
          // Internet Upgrade
          postRequestBody = { coreType: serviceType, itemType: "product", itemId: upgrade.productId ? upgrade.productId : 0 };
        } else {
          // Service Option OR Premium Wifi
          postRequestBody = { coreType: serviceType, itemType: "feature", itemId: upgrade.productId ? upgrade.productId : 0 };
        }
      } else {
        // other core type upgrades
        postRequestBody = { coreType: serviceType, itemType: "product", itemId: upgrade.productId ? upgrade.productId : 0 };
      }
    } else {
      postRequestBody = { coreType: serviceType, itemType: "product", itemId: 0 };
    }

    wrapRequest(async () => {
      window.scroll(0, 0);
      const postCustomerSummaryPageResponse = await postCustomerSummaryPage(postRequestBody);
      const links = postCustomerSummaryPageResponse["_links"];
      const windowLocation = "ec:windowLocation";
      const defaultErrorMessage = "An error occurred in updating your cart.";

      if (postCustomerSummaryPageResponse._embedded) {
        if (postCustomerSummaryPageResponse && postCustomerSummaryPageResponse.ecommerceAction) {
          if (
            postCustomerSummaryPageResponse.ecommerceAction === "prompt" &&
            postCustomerSummaryPageResponse.ecommerceMessageType === "modal" &&
            postCustomerSummaryPageResponse["_embedded"]?.["ec:ad"] &&
            postCustomerSummaryPageResponse["_embedded"]?.["ec:adActions"]
          ) {
            setAdvertisingModalData(postCustomerSummaryPageResponse["_embedded"]["ec:ad"]);
            setAdActions(postCustomerSummaryPageResponse["_embedded"]["ec:adActions"]);
            setShowAdvertisingModal(true);
          } else if (postCustomerSummaryPageResponse.ecommerceAction === "continue" && links && links[windowLocation] && links[windowLocation].href) {
            navigate(links[windowLocation].href);
          } else {
            setToast({
              ...toast,
              show: true,
              variant: "danger",
              header: "Error",
              message: `${postCustomerSummaryPageResponse.message}`,
            });
          }
        } else {
          setToast({
            ...toast,
            show: true,
            variant: "danger",
            header: "Error",
            message: `${defaultErrorMessage}`,
          });
        }
      } else {
        setToast({
          ...toast,
          show: true,
          variant: "danger",
          header: "Error",
          message: `${defaultErrorMessage}`,
        });
      }
    });
  };

  return (
    <div className="customer-summary-container">
      <h2 className="section-top-font">Upgrade Plan</h2>
      {customerSummaryContent && Array.isArray(customerSummaryContent)
        ? customerSummaryContent.map((product: ProductsBundlesModel, index: number) => {
            if (product.coreType === "INT" || product.serviceCoreTypeName === "HSI") {
              return (
                <InternetSection
                  product={product}
                  index={index}
                  labelImageUrl={InternetIcon}
                  label="Internet Plan"
                  postCustomerSummaryRequest={postCustomerSummaryRequest}
                />
              );
            } else if (
              product.coreType === "VID" ||
              product.serviceCoreTypeName === "VID" ||
              product.coreType === "TV" ||
              product.serviceCoreTypeName === "TV"
            ) {
              return (
                <TelevisionSection
                  product={product}
                  index={index}
                  labelImageUrl={TVIcon}
                  label="TV Plan"
                  postCustomerSummaryRequest={postCustomerSummaryRequest}
                />
              );
            } else if (product.coreType === "SEC" || product.serviceCoreTypeName === "SEC") {
              return (
                <SecuritySection
                  product={product}
                  index={index}
                  labelImageUrl={SecurityIcon}
                  label="Security Plan"
                  postCustomerSummaryRequest={postCustomerSummaryRequest}
                />
              );
            } else if (product.coreType === "TEL" || product.serviceCoreTypeName === "TEL") {
              return (
                <PhoneSection
                  product={product}
                  index={index}
                  labelImageUrl={PhoneIcon}
                  label="Phone Plan"
                  postCustomerSummaryRequest={postCustomerSummaryRequest}
                />
              );
            } else {
              return null;
            }
          })
        : null}
      <CancelServicesModal cart={cart} cancelFeatureFlag={cancelFeatureFlag} />
      <AdvertisingModal
        show={showAdvertisingModal}
        handleClose={() => setShowAdvertisingModal(false)}
        handleDecision={(actions) => {
          wrapRequest(async () => {
            let response = await postModalAd(actions);
            setShowAdvertisingModal(false);
            if (response._links["ec:windowLocation"].href) {
              navigate(response._links["ec:windowLocation"].href);
            }
          });
        }}
      />
    </div>
  );
};

export default CustomerSummary;
